import { domainError, type DomainError } from '../shared/domain-error';
import { compareNames, normalizeName } from '../shared/naming';
import { err, ok, type Result } from '../shared/result';
import type {
  DisciplineId,
  JobRoleId,
  MaterialId,
  ProjectId,
  ServiceId,
  ServiceItemId,
  UnitOfMeasureId,
} from '../shared/ids';
import type { MeasureBy } from './geometry';
import { resolveAppliesTo, type AppliesTo } from './applies-to';
import type { IndexAverage, IndexOrigin } from './index-origin';
import type { GuessOrigin } from './service-catalog';
import type { SteelGauge } from './steel';
import type { ItemKind } from './takeoff-line';
import { volumeFrom } from './polygon';
import { TAKEOFF_ERROR, TAKEOFF_ERROR_MESSAGE } from './errors';

/**
 * Serviço — o nível abaixo da disciplina (RN-164).
 *
 * `Alvenaria > Assentamento`. A disciplina já existe desde a Fase 2 e continua sendo a
 * dela a interseção com o pavimento; o serviço é o degrau que faltava para o item saber
 * a que trabalho ele pertence.
 *
 * Vale reler o nome com cuidado: **"atividade" na planilha é isto**, e `Activity` no
 * código continua sendo a interseção pavimento × disciplina, que é outra coisa (ADR-112).
 * Nas telas a segunda passa a se chamar "frente".
 */
export interface Service {
  readonly id: ServiceId;
  readonly disciplineId: DisciplineId;
  /** Ausente = serviço global do sistema, como a disciplina (RN-007). */
  readonly projectId?: ProjectId;
  readonly name: string;
  readonly code?: string;
  /**
   * Como este serviço mede.
   *
   * É o que liga o serviço à geometria: assentamento consome área de **parede**, contada
   * uma vez; reboco consome área de **face**, contada dos dois lados (RN-178). Sem esta
   * escolha, a mesma parede daria o mesmo número para os dois — e um dos dois estaria
   * errado pela metade.
   */
  readonly measureBy: MeasureBy;
  /**
   * A que peça o serviço se aplica — o elemento, ao lado da grandeza (Fase 24, RN-256).
   *
   * Ausente é o implícito da grandeza quando ela tem um (parede, ambiente, nenhum); sem
   * implícito — volume e espala —, é o serviço **pendente**, que não mede nada até alguém
   * escolher pilar ou viga (`serviceElement`, D17).
   */
  readonly appliesTo?: AppliesTo;
  /**
   * Espessura da camada, em metros — o que transforma m² em m³.
   *
   * É do **serviço**, e não da parede: a mesma parede leva chapisco de 5 mm, emboço de
   * 2 cm e gesso de 5 mm, e são três volumes de argamassa do mesmo metro quadrado.
   * Ausente = serviço que não tem espessura (assentamento de porcelanato, louça, porta),
   * e ausente **não** é zero: um mede em m³ e o outro não mede em m³ nenhum.
   */
  readonly layerThickness?: number;
  /**
   * Margem para cada lado do vão, em metros (Fase 24, RN-308).
   *
   * A soleira e o peitoril passam alguns centímetros do vão, para cada lado, e o número do
   * serviço passa a ser Σ (largura + 2 × margem) × quantos. É do **serviço**, como a
   * espessura: o vão é o mesmo, e a soleira de granito e a de mármore podem passar dele de
   * jeitos diferentes. Só conta na largura de porta, janela e passagem
   * (`countedSideMargin`); ausente é zero — ao contrário da espessura, aqui zero e ausente são
   * a mesma soleira, rente ao vão.
   */
  readonly sideMargin?: number;
  /**
   * A unidade em que este serviço é medido (RN-232, Fase 21).
   *
   * Ela era da disciplina, e a disciplina nunca soube responder: alvenaria mede parede em
   * m² e verga em unidade, e as duas são alvenaria. Mora aqui porque é o serviço que tem
   * método — `measureBy` diz **de onde** o número sai da geometria, e a unidade diz **em
   * que** ele é lido por quem confere.
   *
   * Ausente ainda existe: serviço importado de planilha chega sem unidade, e quem mede é
   * recusado até alguém escolher uma. Vazio e "medido em nada" continuam sendo coisas
   * diferentes.
   */
  readonly unitOfMeasureId?: UnitOfMeasureId;
  readonly position: number;
  readonly isActive: boolean;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly deletedAt?: string;
}

export interface CreateServiceInput {
  readonly id: ServiceId;
  readonly disciplineId: DisciplineId;
  readonly name: string;
  readonly measureBy: MeasureBy;
  /** Ausente = o implícito da grandeza; volume e espala sem ele nascem pendentes. */
  readonly appliesTo?: AppliesTo;
  readonly layerThickness?: number;
  readonly sideMargin?: number;
  readonly unitOfMeasureId?: UnitOfMeasureId;
  readonly projectId?: ProjectId;
  readonly code?: string;
  readonly position?: number;
  readonly now: string;
}

export function createService(input: CreateServiceInput): Result<Service, DomainError> {
  const name = input.name.trim();
  if (name.length === 0) {
    return err(
      domainError(
        TAKEOFF_ERROR.SERVICE_NAME_REQUIRED,
        TAKEOFF_ERROR_MESSAGE[TAKEOFF_ERROR.SERVICE_NAME_REQUIRED]!,
      ),
    );
  }
  // O par é conferido na entrada (RN-256): gravado inválido, ele não mediria nada e
  // pareceria que mede.
  const elemento = resolveAppliesTo(input.measureBy, input.appliesTo);
  if (!elemento.ok) return elemento;
  return ok({
    id: input.id,
    disciplineId: input.disciplineId,
    name,
    measureBy: input.measureBy,
    ...(elemento.value === undefined ? {} : { appliesTo: elemento.value }),
    position: input.position ?? 0,
    isActive: true,
    createdAt: input.now,
    updatedAt: input.now,
    ...(input.layerThickness === undefined ? {} : { layerThickness: input.layerThickness }),
    ...(input.sideMargin === undefined ? {} : { sideMargin: input.sideMargin }),
    ...(input.unitOfMeasureId === undefined ? {} : { unitOfMeasureId: input.unitOfMeasureId }),
    ...(input.projectId === undefined ? {} : { projectId: input.projectId }),
    ...(input.code === undefined ? {} : { code: input.code }),
  });
}

export interface EditServiceInput {
  /**
   * Trocar a disciplina é mover o serviço de gaveta, e é operação legítima: quem cadastrou
   * `VERGAS E CONTRAVERGAS` em Estrutural, e não em Alvenaria, corrige aqui. Ausente = fica
   * onde está.
   */
  readonly disciplineId?: DisciplineId;
  readonly name: string;
  readonly measureBy: MeasureBy;
  /**
   * Ausente **não** limpa, ao contrário dos opcionais abaixo: é "não disse", e fica o
   * elemento gravado enquanto ele casar com a grandeza (`resolveAppliesTo`). Pendente não
   * é coisa que alguém escolha — é o que sobra quando ninguém escolheu.
   */
  readonly appliesTo?: AppliesTo;
  readonly code?: string;
  readonly layerThickness?: number;
  /** Como a espessura: ausente limpa (RN-308). */
  readonly sideMargin?: number;
  readonly unitOfMeasureId?: UnitOfMeasureId;
  readonly now: string;
}

/**
 * Corrige o cadastro do serviço — o nome incluído.
 *
 * O nome é a chave natural (RN-230), e ainda assim ele é editável: corrigir
 * `ASSENTAMENTO DE ALVENRIA` é edição, não um serviço novo, e quem já mediu na grafia
 * errada continua apontando para o mesmo id. Quem cuida da unicidade é o caso de uso, que
 * é quem alcança os outros serviços da disciplina.
 *
 * Os opcionais **saem** antes de voltar: ausente na entrada é ausente no serviço, e
 * não "fica o de antes". Espessura em branco não é zero — é serviço que não mede em m³
 * nenhum —, e quem limpou o campo na tela quis dizer isso (RN-237: a mudança vale para
 * frente; quantidade e verificação já gravadas descrevem o que aconteceu, e não mudam).
 */
export function editService(
  service: Service,
  input: EditServiceInput,
): Result<Service, DomainError> {
  const name = input.name.trim();
  if (name.length === 0) {
    return err(
      domainError(
        TAKEOFF_ERROR.SERVICE_NAME_REQUIRED,
        TAKEOFF_ERROR_MESSAGE[TAKEOFF_ERROR.SERVICE_NAME_REQUIRED]!,
      ),
    );
  }

  const elemento = resolveAppliesTo(input.measureBy, input.appliesTo, service.appliesTo);
  if (!elemento.ok) return elemento;

  const {
    code: _code,
    layerThickness: _espessura,
    sideMargin: _margem,
    unitOfMeasureId: _unidade,
    appliesTo: _elemento,
    ...semOpcionais
  } = service;

  return ok({
    ...semOpcionais,
    disciplineId: input.disciplineId ?? service.disciplineId,
    name,
    measureBy: input.measureBy,
    ...(elemento.value === undefined ? {} : { appliesTo: elemento.value }),
    updatedAt: input.now,
    ...(input.code === undefined ? {} : { code: input.code }),
    ...(input.layerThickness === undefined ? {} : { layerThickness: input.layerThickness }),
    ...(input.sideMargin === undefined ? {} : { sideMargin: input.sideMargin }),
    ...(input.unitOfMeasureId === undefined ? {} : { unitOfMeasureId: input.unitOfMeasureId }),
  });
}

/**
 * Liga e desliga o serviço no catálogo.
 *
 * Desligar não é excluir, e a diferença importa mais aqui do que na disciplina: o serviço
 * desativado sai das opções de quem planeja, e **fica** na verificação, na quantidade e na
 * não conformidade que já foram gravadas (RN-234). Elas descrevem o que aconteceu, e o que
 * aconteceu não deixa de ter acontecido porque o cadastro foi aposentado.
 */
export function setServiceActive(service: Service, isActive: boolean, now: string): Service {
  return { ...service, isActive, updatedAt: now };
}

/** O nome já está tomado nesta disciplina (RN-230). */
export function serviceNameTakenError(name: string): DomainError {
  return domainError(
    TAKEOFF_ERROR.SERVICE_NAME_TAKEN,
    TAKEOFF_ERROR_MESSAGE[TAKEOFF_ERROR.SERVICE_NAME_TAKEN]!,
    { name },
  );
}

/** Chave natural do serviço dentro da disciplina (RN-164). */
export function serviceNaturalKey(disciplineId: DisciplineId, name: string): string {
  return `${disciplineId}|${normalizeName(name)}`;
}

/** `"Alvenaria > Assentamento"` — como o serviço aparece na tela e na exportação. */
export function describeService(disciplineName: string, service: Pick<Service, 'name'>): string {
  return `${disciplineName} > ${service.name}`;
}

export function compareServices(a: Service, b: Service): number {
  return a.position - b.position || compareNames(a.name, b.name);
}

/**
 * O serviço está disponível para esta obra? (mesma regra da disciplina, RN-007)
 *
 * Global serve a todas; o de obra serve só à dela.
 */
export function isAvailableForProject(service: Service, projectId: ProjectId): boolean {
  return service.projectId === undefined || service.projectId === projectId;
}

// ── O que cada serviço consome ──────────────────────────────────────────────────

/**
 * O item do serviço, nesta obra (RN-165, RN-249) — o que vem da planilha, e o que alguém
 * acrescentou na tela.
 *
 * Até a Fase 24 era só o vínculo serviço → `Material`, e por isso nunca saiu do
 * navegador: material tem código obrigatório e único (RN-011), e 140 dos 218 itens da
 * planilha do cliente não têm código. Agora o item **aponta um material ou é descrição
 * livre** (RN-250): tem descrição, unidade, classe e código de compras próprios, e o
 * material é o vínculo que se resolve pelo código quando o cadastro tem um.
 *
 * **A mão de obra é item** (RN-252). O comentário antigo daqui dizia o contrário — "a
 * função já diz quais disciplinas executa" — e confundia duas perguntas: a função diz
 * **quem pode** executar; o item diz **quanto** de hora o serviço consome. O item de mão
 * de obra aponta uma função, e a função continua sendo o cadastro de quem pode.
 *
 * É **da obra** mesmo quando o serviço é global (RN-254): o assentamento global da Vienna
 * não pode levar o tijolo que a obra do lado nunca comprou.
 */
export interface ServiceItem {
  readonly id: ServiceItemId;
  readonly projectId: ProjectId;
  readonly serviceId: ServiceId;
  /** Como o item se chama — a identidade dele dentro do serviço, normalizada (RN-251). */
  readonly description: string;
  /** O código de compras, como a planilha ou o catálogo do cliente o escrevem. */
  readonly purchaseCode?: string;
  /**
   * O material do cadastro que tem o mesmo código — presente só quando existe um.
   *
   * Ausente não é erro: é o item "sem código de compras", ou com um código que o cadastro
   * ainda não conhece. A consolidação soma pelo código mesmo assim (RN-250).
   */
  readonly materialId?: MaterialId;
  /** Texto da planilha — "SC 20KG", "UNI". Mão de obra é sempre lida em HH. */
  readonly unit?: string;
  readonly payment?: string;
  readonly kind: ItemKind;
  readonly kindOrigin: GuessOrigin;
  /** A função que executa — só no item de mão de obra, e só uma da disciplina (RN-252). */
  readonly jobRoleId?: JobRoleId;
  /** O que cada aba da planilha declarou, pelo nome da aba. */
  readonly quantityBySheet: Readonly<Record<string, number>>;
  /**
   * Quanto do item por unidade do medido do serviço (Fase 24, D7) — do cadastro, para
   * todas as tipologias da obra. Ausente, ou zero, é "sem índice".
   */
  readonly consumptionIndex?: number;
  /** De onde veio o índice (Fase 40, RN-520). Ausente é o índice de antes: digitado. */
  readonly indexOrigin?: IndexOrigin;
  /** A foto da média quando o índice foi gravado (D8). */
  readonly indexAverage?: IndexAverage;
  /** A perda, em por cento (D8). Nunca na mão de obra — o banco também recusa. */
  readonly lossPercent?: number;
  /**
   * A bitola, quando o item é aço (Fase 27, RN-366). Só no CORTE E DOBRA DE FERRAGEM, e nunca
   * na mão de obra — o domínio recusa, e o banco também (`0053`). O item com bitola não usa o
   * índice: reparte o medido pela participação da bitola na taxa da disciplina (RN-378).
   */
  readonly steelGauge?: SteelGauge;
  /** A ordem da planilha, que é a ordem em que quem conferiu leu. */
  readonly position: number;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly deletedAt?: string;
}

export function serviceItemNaturalKey(serviceId: ServiceId, materialId: MaterialId): string {
  return `${serviceId}|${materialId}`;
}

/**
 * Os itens que cada serviço autoriza, indexados uma vez.
 *
 * A tela de lançamento pergunta isso a cada item digitado; varrer a lista inteira em cada
 * pergunta transformaria uma planilha de mil vínculos em mil comparações por tecla.
 */
export class ServiceItemIndex {
  private constructor(
    private readonly byService: ReadonlyMap<ServiceId, ReadonlySet<MaterialId>>,
  ) {}

  static build(links: readonly ServiceItem[]): ServiceItemIndex {
    const map = new Map<ServiceId, Set<MaterialId>>();
    for (const link of links) {
      if (link.deletedAt !== undefined) continue;
      // O item sem material — descrição livre ou mão de obra — não autoriza material
      // nenhum: a pergunta daqui é "este material pode entrar neste serviço?".
      if (link.materialId === undefined) continue;
      let materials = map.get(link.serviceId);
      if (materials === undefined) {
        materials = new Set();
        map.set(link.serviceId, materials);
      }
      materials.add(link.materialId);
    }
    return new ServiceItemIndex(map);
  }

  /**
   * Este item pode entrar neste serviço?
   *
   * Serviço **sem** nenhum vínculo responde `false`, nunca "tudo" — a mesma postura de
   * `JobRoleDisciplineIndex`. Tratar a ausência de cadastro como autorização geral faria
   * a trava desaparecer justamente nos serviços que a planilha esqueceu, que são os que
   * mais precisam dela.
   */
  allows(serviceId: ServiceId, materialId: MaterialId): boolean {
    return this.byService.get(serviceId)?.has(materialId) ?? false;
  }

  materialsOf(serviceId: ServiceId): readonly MaterialId[] {
    return [...(this.byService.get(serviceId) ?? [])] as MaterialId[];
  }
}

export function itemNotAllowedError(serviceId: ServiceId, materialId: MaterialId): DomainError {
  return domainError(
    TAKEOFF_ERROR.ITEM_NOT_ALLOWED,
    TAKEOFF_ERROR_MESSAGE[TAKEOFF_ERROR.ITEM_NOT_ALLOWED]!,
    { serviceId, materialId },
  );
}

/**
 * O m³ que este serviço consome sobre a área medida.
 *
 * Devolve `undefined` para serviço sem espessura — que é diferente de zero. Zero seria
 * "não gasta volume"; ausente é "esta conta não se aplica", e a tela não deve mostrar
 * `0,000 m³` de porta.
 */
export function serviceVolume(
  service: Pick<Service, 'layerThickness'>,
  measuredArea: number,
): number | undefined {
  if (service.layerThickness === undefined) return undefined;
  return volumeFrom(measuredArea, service.layerThickness);
}
